import React from 'react'
import cn from 'classnames'
import Mask from 'js/components/Mask'
import NutritionHeader from './components/NutritionHeader'
import { runQuery } from 'js/dato'
import withSSR from 'js/components/withSSR/withSSR'
import NutritionStartDiets from './components/NutritionStartDiets'
import NutritionStartRecipes from './components/NutritionStartRecipes'
import NutritionStartStories from './components/NutritionStartStories'
import NutritionStartNutritionTime from './components/NutritionStartNutritionTime'
import NutritionStartGoals from './components/NutritionStartGoals'
import NutritionStartCategories from './components/NutritionStartCategories'
import SymbolHeading from './components/SymbolHeading'
import Heat from './assets/heat.svg?svgr-webpack'
import FoodCover from './assets/food-cover.svg?svgr-webpack'
import SaucePanHot from './assets/sauce-pan-hot.svg?svgr-webpack'
import Television from './assets/television.svg?svgr-webpack'
import BusinessTarget from './assets/business-target.svg?svgr-webpack'
import Tag from './assets/price-tag.svg?svgr-webpack'
import NutritionFooter from './components/NutritionFooter'
import AppPromo from './components/AppPromo'
import { Title } from 'react-head'
import {
  LatestNutritionArticles_latestArticles,
  LatestNutritionArticles_nutritionTime,
  LatestNutritionArticles_start,
  LatestNutritionArticles_start_categories,
  LatestNutritionArticles_start_featuredRecipes,
  LatestNutritionArticles_start_featuredStory,
} from './types/LatestNutritionArticles'
import { NonNullableObject, RecursiveNonNullableObject } from 'typings'

type SectionProps = {
  children: React.ReactNode
  className?: string
  width?: 'narrow' | 'medium' | 'wide'
}

function Section({ children, className, width }: SectionProps) {
  return (
    <div className={cn('bg-white pb-48 lg:pb-72', className)}>
      <div
        className={cn(
          'px-20 mx-auto text-center',
          width === 'narrow' && 'max-w-968',
          (width === 'medium' || !width) && 'max-w-1280',
          width === 'wide' && 'max-w-1680'
        )}
      >
        {children}
      </div>
    </div>
  )
}

type Props = {
  data: {
    latestArticles: NonNullableObject<LatestNutritionArticles_latestArticles>[]
    start: LatestNutritionArticles_start
    nutritionTime: RecursiveNonNullableObject<LatestNutritionArticles_nutritionTime>
  }
  isLoading: boolean
}

function NutritionStart({ data, isLoading }: Props) {
  if (isLoading) return null

  const { start, latestArticles, nutritionTime } = data
  const { featuredStory, goals, diets, featuredRecipes, categories } = start

  return (
    <>
      <Title>Nutrition Explained - Lifesum</Title>
      <Mask className="bg-Bg">
        <NutritionHeader />
        <div className="bg-BgSub pt-48 pb-80 lg:pt-72 lg:pb-128 text-center px-16">
          <h1 className="type-large-fat text-green">Nutrition Explained</h1>
          <div className="type-17 md:type-24 lg:type-28 leading-body max-w-576 mx-auto mt-12 md:mt-16">
            Learn about nutrition from Lifesum’s in-house nutritionists and
            invited experts.
          </div>
        </div>
      </Mask>
      <Section className="-mt-40" width="narrow">
        <SymbolHeading symbol={Heat}>Latest stories</SymbolHeading>
        <NutritionStartStories
          featuredStory={
            featuredStory as NonNullableObject<LatestNutritionArticles_start_featuredStory>
          }
          latestArticles={latestArticles}
        />
      </Section>
      <Section>
        <SymbolHeading symbol={FoodCover} symbolStyle={{ marginTop: -2 }}>
          Diets
        </SymbolHeading>
        <NutritionStartDiets diets={diets} />
      </Section>

      <Section>
        <div
          className={`
          grid lg:grid-cols-2 gap-20
          mx-auto max-w-968 `}
        >
          <div>
            <SymbolHeading
              symbol={SaucePanHot}
              symbolStyle={{ margin: '-2 0 0 3px' }}
            >
              Featured recipes
            </SymbolHeading>
            <p className="mb-24 -mt-16 lg:-mt-24 leading-body">
              Our team of professional chefs and nutritionists know how to make
              healthy eating delicious!
            </p>
            <NutritionStartRecipes
              recipes={
                featuredRecipes as NonNullableObject<LatestNutritionArticles_start_featuredRecipes>[]
              }
            />
          </div>
          <div className="relative -mx-20">
            <div
              className={`
              absolute lg:relative left-1/2
              transform -translate-x-1/2 
              z-10 -mt-40 lg:mt-0 mb-48 lg:mb-0
              w-full px-20`}
            >
              <SymbolHeading
                symbol={Television}
                symbolStyle={{ marginTop: -1 }}
              >
                Nutrition time
              </SymbolHeading>
            </div>
            <div className="bg-BgSub lg:bg-Bg">
              <p className="mb-24 lg:-mt-24 leading-body pt-64 lg:pt-0 mx-20">
                Sit back, relax and let us guide you through the basics of
                nutrition in this series of short videos.
              </p>
              <NutritionStartNutritionTime {...nutritionTime} />
            </div>
          </div>
        </div>
      </Section>
      <Section>
        <SymbolHeading symbol={BusinessTarget} symbolStyle={{ marginLeft: 4 }}>
          What’s your goal?
        </SymbolHeading>
        <p className="mb-24 lg:mb-48 -mt-16 lg:-mt-24 leading-body">
          Browse articles based on your health goal.
        </p>
        <NutritionStartGoals goals={goals} />
      </Section>
      <Section>
        <div className="md:py-40 lg:pt-80">
          <AppPromo />
        </div>
      </Section>
      <Section>
        <SymbolHeading symbol={Tag}>Categories</SymbolHeading>
        <NutritionStartCategories
          categories={
            categories as NonNullableObject<LatestNutritionArticles_start_categories>[]
          }
        />
      </Section>
      {/*<Section>
        <SymbolHeading symbol={Instagram}>Instagram</SymbolHeading>
        <div
          className={`
          grid grid-cols-3 grid-rows-3 gap-2
          text-center
          px-20 mx-auto max-w-512 `}
        >
          {[...Array(9)].map((_, index) => (
            <img
              key={index}
              src={`https://source.unsplash.com/random/105x105?${index}`}
              alt=""
              className="w-full"
            />
          ))}
          <div className="col-span-3 mt-24">
            <MoreButton use="a" href="https://www.instagram.com/lifesum/">
              More from @Lifesum
            </MoreButton>
          </div>
        </div>
      </Section> */}
      <NutritionFooter />
    </>
  )
}

const query = gql`
  query LatestNutritionArticles {
    latestArticles: allNutritionArticles(
      first: 4
      orderBy: _createdAt_DESC
      filter: { archived: { eq: false } }
    ) {
      id
      title
      image {
        url(
          imgixParams: { w: 240, h: 160, fm: jpg, fit: crop, crop: focalpoint }
        )
      }
      slug
    }
    start: nutritionStart {
      featuredStory {
        id
        title
        excerpt
        image {
          url(imgixParams: { w: 640, h: 384, fit: crop, crop: focalpoint })
        }
        desktopImage: image {
          url(imgixParams: { w: 640, h: 640, fit: crop, crop: focalpoint })
        }
        categories {
          name
        }
        slug
        content {
          ... on ArticleTextRecord {
            text
          }
        }
      }
      signupMessageText
      diets {
        id
        name
        slug
        image {
          url(
            imgixParams: {
              w: 375
              h: 250
              fm: jpg
              fit: crop
              crop: focalpoint
            }
          )
        }
      }
      goals {
        id
        name
        description
        slug
        image {
          url
        }
      }
      featuredRecipes {
        id
        recipeName
        slug
        image {
          responsiveImage(
            imgixParams: {
              w: 480
              h: 270
              fm: jpg
              fit: crop
              crop: focalpoint
            }
          ) {
            srcSet
            webpSrcSet
            src
            alt
            title
            base64
            width
            height
          }
        }
        intro
        time
        calories
      }
      categories {
        id
        name
        slug
      }
    }
    nutritionTime(orderBy: episodeNumber_ASC) {
      name
      description
      video {
        thumbnailUrl
        url
      }
    }
  }
`

NutritionStart.getInitialProps = () => {
  return runQuery(query)
}

export default withSSR(NutritionStart)
